import React from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { UiDraft } from '../utils/ui';
import AppDateTimeField from './AppDateTimeField';

type UiIncidentFieldsProps = {
  value: UiDraft;
  onChange: (patch: Partial<UiDraft>) => void;
  /** When standalone, always show fields (no yes/no toggle). */
  variant?: 'embedded' | 'standalone';
  /** After a failed save, outline empty required fields in red. */
  showErrors?: boolean;
};

const UiIncidentFields: React.FC<UiIncidentFieldsProps> = ({
  value,
  onChange,
  variant = 'embedded',
  showErrors = false,
}) => {
  const isStandalone = variant === 'standalone';
  const showFields = isStandalone || value.has_ui;
  const timeMissing = showErrors && showFields && !value.time_of_incident.trim();
  const locationMissing = showErrors && showFields && !value.location_of_incident.trim();
  const descriptionMissing = showErrors && showFields && !value.description_of_incident.trim();

  return (
  <>
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mt: isStandalone ? 0 : 1, fontWeight: 600, color: '#2D4B73' }}>
        Unusual Incident (UI)
      </Typography>
    </Grid>
    {!isStandalone && (
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth size="small">
        <InputLabel>Was there an Unusual Incident?</InputLabel>
        <Select
          value={value.has_ui ? 'Y' : 'N'}
          label="Was there an Unusual Incident?"
          onChange={e => onChange({ has_ui: e.target.value === 'Y', mui: e.target.value === 'Y' ? value.mui : false })}
        >
          <MenuItem value="N">No</MenuItem>
          <MenuItem value="Y">Yes</MenuItem>
        </Select>
      </FormControl>
    </Grid>
    )}
    {showFields && (
      <>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Major Unusual Incident (MUI)?</InputLabel>
            <Select
              value={value.mui ? 'Y' : 'N'}
              label="Major Unusual Incident (MUI)?"
              onChange={e => onChange({ mui: e.target.value === 'Y' })}
            >
              <MenuItem value="N">No</MenuItem>
              <MenuItem value="Y">Yes</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <AppDateTimeField
            dateLabel="Incident date"
            timeLabel="Incident time"
            value={value.time_of_incident}
            onChange={time_of_incident => onChange({ time_of_incident })}
            required
            size="small"
            error={timeMissing}
            helperText={timeMissing ? 'Time of incident is required' : undefined}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
              required
              label="Location of incident"
              value={value.location_of_incident}
              onChange={e => onChange({ location_of_incident: e.target.value })}
              error={locationMissing}
              helperText={locationMissing ? 'Location of incident is required' : undefined}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
              required
              label="Description of incident"
              value={value.description_of_incident}
              onChange={e => onChange({ description_of_incident: e.target.value })}
              error={descriptionMissing}
              helperText={descriptionMissing ? 'Description of incident is required' : undefined}
              multiline
              rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
            label="Description of injury (if any)"
            value={value.description_of_injury}
            onChange={e => onChange({ description_of_injury: e.target.value })}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
            label="Causes or contributing factors"
            value={value.causes_or_contributing_factors}
            onChange={e => onChange({ causes_or_contributing_factors: e.target.value })}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            size="small"
            fullWidth
            label="Actions taken"
            value={value.actions_taken}
            onChange={e => onChange({ actions_taken: e.target.value })}
            multiline
            rows={2}
          />
        </Grid>
      </>
    )}
  </>
  );
};

export default UiIncidentFields;
